// MiPlanPage.js — /mi-plan: «Mi plan de formación» (Learning, alumnos con subrol especial).
// Arriba, sobre el banner de portadas, el nombre del plan y «Mi avance en el plan de formación»:
// un anillo con el porcentaje (lecciones completadas de todos los cursos del plan ÷ total) y lo
// que falta para el certificado final. Debajo, los cursos del plan (sin orden obligatorio) con su
// avance y, al final, el certificado final del plan: bloqueado hasta completarlo, luego para
// descargar. Quien no tiene plan (sin subrol, Inactivo, otros proyectos) vuelve a la portada.
import React, { useEffect, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { Link, Navigate } from 'react-router-dom';
import { useProject } from '../contexts/ProjectContext';
import useMiPlan from '../hooks/useMiPlan';
import MuroCursos from '../components/landing/sections/MuroCursos';
import { BACKEND_BASE, certificadoService } from '../services/api';

const FUENTE = "'Bricolage Grotesque', 'Inter', system-ui, sans-serif";
const urlImg = (v) => (!v ? '' : (/^https?:\/\//.test(v) || v.startsWith('/') ? v : `${BACKEND_BASE}/${v}`));
const horas = (seg) => {
  const m = Math.round((seg || 0) / 60);
  if (m < 60) return `${m} min`;
  return `${Math.floor(m / 60)} h${m % 60 ? ` ${m % 60} min` : ''}`;
};
const haceCuanto = (f) => {
  if (!f) return '';
  const dias = Math.floor((Date.now() - new Date(String(f).replace(' ', 'T') + 'Z').getTime()) / 86400000);
  if (dias <= 0) return 'hoy';
  if (dias === 1) return 'ayer';
  if (dias < 30) return `hace ${dias} días`;
  return `hace ${Math.round(dias / 30)} ${Math.round(dias / 30) === 1 ? 'mes' : 'meses'}`;
};

const Raiz = styled.div`
  --acento: ${p => p.$acento}; --fondo: ${p => p.$fondo};
  min-height: 100vh; background: var(--fondo); color: #d5d9df; font-family: ${FUENTE};
`;
const Cabecera = styled.header`
  position: relative; overflow: hidden; isolation: isolate; min-height: clamp(440px, 62vh, 600px);
  display: grid; grid-template-columns: minmax(0, 1fr) auto; align-items: end; gap: 2rem 4rem;
  padding: clamp(8rem, 12vw, 9.5rem) 4vw clamp(2.2rem, 4vw, 3.2rem);
  > img.portada { position: absolute; inset: 0; z-index: -2; width: 100%; height: 100%; object-fit: cover; opacity: .55; }
  .eyebrow { display: inline-flex; align-items: center; gap: .6rem; font-size: .95rem; font-weight: 600; color: #e7eaee; }
  .eyebrow::before { content: ''; width: 28px; height: 2px; background: var(--acento); }
  h1 { margin: .9rem 0 0; max-width: 18ch; font-size: clamp(2.6rem, 7vw, 5.4rem); font-weight: 800; font-stretch: 76%; line-height: .92; letter-spacing: -.02em; color: #fff; text-wrap: balance; }
  .desc { margin: 1.1rem 0 0; max-width: 44rem; font-size: 1.08rem; line-height: 1.55; color: #d0d5dc; }
  dl { display: flex; flex-wrap: wrap; gap: .6rem; margin: 1.5rem 0 0; }
  dl div { display: flex; align-items: baseline; gap: .45rem; padding: .55rem 1rem; border-radius: 999px;
    background: rgba(0,0,0,.42); box-shadow: inset 0 0 0 1px rgba(255,255,255,.1); backdrop-filter: blur(8px); -webkit-backdrop-filter: blur(8px); }
  dt { order: 2; font-size: .88rem; color: #c3c9d1; }
  dd { margin: 0; font-size: 1.15rem; font-weight: 800; color: #fff; font-variant-numeric: tabular-nums; }
  @media (max-width: 820px) { grid-template-columns: 1fr; }
`;
const llenar = keyframes`from { stroke-dashoffset: var(--total); }`;
const Avance = styled.figure`
  margin: 0; display: grid; justify-items: center; gap: .6rem; text-align: center;
  .t { font-size: .95rem; font-weight: 700; color: #fff; }
  .anillo { position: relative; width: clamp(170px, 18vw, 220px); aspect-ratio: 1; border-radius: 50%;
    background: rgba(0,0,0,.45); backdrop-filter: blur(8px); -webkit-backdrop-filter: blur(8px); }
  svg { width: 100%; height: 100%; transform: rotate(-90deg); }
  circle { fill: none; stroke-width: 7; }
  .base { stroke: rgba(255,255,255,.14); }
  .valor { stroke: ${p => (p.$listo ? '#3ddc84' : 'var(--acento)')}; stroke-linecap: round; stroke-dasharray: var(--total); stroke-dashoffset: var(--resta);
    animation: ${llenar} 1.4s .2s cubic-bezier(.3,.7,.2,1) both; }
  .cifra { position: absolute; inset: 0; display: grid; place-content: center; }
  .cifra b { font-size: clamp(3rem, 5.5vw, 4.2rem); font-weight: 800; font-stretch: 76%; line-height: 1; color: #fff; font-variant-numeric: tabular-nums; }
  .cifra b small { font-size: .42em; color: #aab1bb; }
  figcaption { max-width: 16rem; font-size: .9rem; color: #c3c9d1; }
  figcaption strong { color: ${p => (p.$listo ? '#3ddc84' : '#fff')}; }
  @media (max-width: 820px) { justify-items: start; text-align: left; .anillo { width: 150px; } }
  @media (prefers-reduced-motion: reduce) { .valor { animation: none; } }
`;

const Cuerpo = styled.main`
  max-width: 1100px; padding: clamp(2rem, 4vw, 3rem) 4vw clamp(4rem, 7vw, 6rem);
  h2 { display: flex; align-items: baseline; gap: .6rem; margin: 0 0 .4rem; font-size: clamp(1.4rem, 2.4vw, 1.8rem); font-weight: 800; font-stretch: 85%; color: #fff; }
  h2 small { font-size: .9rem; font-weight: 600; font-stretch: 100%; color: #8a929d; }
  > p { margin: 0 0 1.2rem; color: #8a929d; font-size: .95rem; }
`;
const Cursos = styled.ol`
  list-style: none; margin: 0; padding: 0; border-top: 1px solid rgba(255,255,255,.1);
`;
const Curso = styled.li`
  display: grid; grid-template-columns: 34px 132px minmax(0, 1fr) auto; gap: 0 1.1rem; align-items: center;
  padding: 1rem 0; border-bottom: 1px solid rgba(255,255,255,.1);
  .estado { width: 32px; height: 32px; border-radius: 50%; display: grid; place-items: center; }
  .estado svg { width: 16px; height: 16px; }
  .estado.listo { background: #3ddc84; color: #06210f; }
  .estado.curso { box-shadow: inset 0 0 0 2px var(--acento); color: #fff; }
  .estado.nuevo { box-shadow: inset 0 0 0 2px #3a3a3a; color: #6f7680; }
  .img { aspect-ratio: 16 / 9; border-radius: 6px; overflow: hidden; background: #222; }
  .img img { width: 100%; height: 100%; object-fit: cover; display: block; }
  h3 { margin: 0; font-size: 1.05rem; font-weight: 700; color: #fff; line-height: 1.3; }
  .barra { margin-top: .55rem; height: 5px; max-width: 340px; border-radius: 3px; background: rgba(255,255,255,.12); overflow: hidden; }
  .barra i { display: block; height: 100%; background: ${p => (p.$listo ? '#3ddc84' : 'var(--acento)')}; }
  .det { display: flex; flex-wrap: wrap; gap: .2rem 1rem; margin-top: .45rem; font-size: .86rem; color: #9aa1ab; }
  .det b { color: #e7eaee; font-weight: 600; }
  a.ir { display: inline-flex; align-items: center; gap: .45rem; height: 42px; padding: 0 1.1rem; border-radius: 8px; text-decoration: none;
    font-weight: 700; font-size: .92rem; white-space: nowrap; }
  a.ir.blanco { background: #fff; color: #111; }
  a.ir.blanco:hover { background: rgba(255,255,255,.86); }
  a.ir.gris { background: rgba(109,109,110,.6); color: #fff; }
  a.ir.gris:hover { background: rgba(109,109,110,.4); }
  a.ir:focus-visible { outline: 2px solid var(--acento); outline-offset: 3px; }
  a.ir svg { width: 16px; height: 16px; }
  @media (max-width: 720px) {
    grid-template-columns: 34px minmax(0, 1fr); row-gap: .7rem;
    .img { display: none; }
    a.ir { grid-column: 2; justify-self: start; }
  }
`;
const Certificado = styled.section`
  margin-top: 2.2rem; display: grid; grid-template-columns: 150px minmax(0, 1fr) auto; gap: 1.4rem; align-items: center;
  padding: 1.3rem 1.4rem; border-radius: 12px;
  background: ${p => (p.$listo ? 'linear-gradient(120deg, rgba(61,220,132,.14), #1d1d1d 60%)' : 'linear-gradient(120deg, color-mix(in srgb, var(--acento) 14%, transparent), #1d1d1d 60%)')};
  box-shadow: inset 0 0 0 1px ${p => (p.$listo ? 'rgba(61,220,132,.4)' : 'color-mix(in srgb, var(--acento) 40%, transparent)')};
  .diploma { position: relative; aspect-ratio: 1.41; border-radius: 4px; background: #f3efe6; display: grid; place-items: center;
    box-shadow: 0 12px 26px -12px #000; ${p => (p.$listo ? '' : 'filter: grayscale(1) brightness(.5);')} }
  .diploma::before { content: ''; position: absolute; inset: 7px; border: 1px solid #c8b58a; border-radius: 2px; }
  .diploma span { font-size: .62rem; font-weight: 800; color: #6b5a2e; letter-spacing: .1em; text-transform: uppercase; }
  .candado { position: absolute; width: 34px; height: 34px; border-radius: 50%; display: grid; place-items: center; background: #141414; color: #fff;
    box-shadow: 0 0 0 2px rgba(255,255,255,.2); filter: none; }
  .candado svg { width: 16px; height: 16px; }
  .eyebrow { font-size: .85rem; font-weight: 700; color: ${p => (p.$listo ? '#3ddc84' : '#ff8a8f')}; }
  h3 { margin: .3rem 0 0; font-size: 1.3rem; font-weight: 800; font-stretch: 85%; color: #fff; }
  p { margin: .4rem 0 0; font-size: .95rem; color: #c3c9d1; line-height: 1.5; }
  button, a.btn { display: inline-flex; align-items: center; height: 46px; padding: 0 1.3rem; border: 0; border-radius: 8px; cursor: pointer;
    font: inherit; font-weight: 700; text-decoration: none; background: #fff; color: #111; white-space: nowrap; }
  button:disabled { opacity: .6; cursor: default; }
  @media (max-width: 720px) { grid-template-columns: 96px minmax(0, 1fr); button, a.btn { grid-column: 1 / -1; justify-self: start; } }
`;

const Ico = {
  ok: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12.5l4.5 4.5L19 7.5" /></svg>,
  play: <svg viewBox="0 0 24 24" aria-hidden="true"><path fill="currentColor" d="M7.5 5v14l11.5-7Z" /></svg>,
  medio: <svg viewBox="0 0 24 24" aria-hidden="true"><path fill="currentColor" d="M12 4a8 8 0 0 1 0 16Z" /></svg>,
  candado: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><rect x="5" y="11" width="14" height="10" rx="2" /><path d="M8 11V8a4 4 0 0 1 8 0v3" /></svg>,
};

const MiPlanPage = () => {
  const { proyecto } = useProject();
  const mp = useMiPlan({ detalle: true });
  const cat = (proyecto?.config_json?.landing_secciones || []).find(s => s.tipo === 'catalogo_streaming')?.config || {};
  const acento = cat.color_acento || '#E50914';
  const fondo = cat.color_fondo || '#141414';
  const [descargando, setDescargando] = useState(false);
  useEffect(() => { document.title = 'Mi plan de formación'; }, []);

  if (!mp.listo) return <Raiz $acento={acento} $fondo={fondo} />;
  if (mp.modo !== 'plan') return <Navigate to="/" replace />;

  const av = mp.avance || { hechas: 0, total: 0, pct: 0, cursos_total: 0, cursos_terminados: 0, tiempo_seg: 0 };
  const lista = mp.detalle || [];
  const cert = mp.certificado || null;           // { configurado, emitido, id, codigo }
  const completo = lista.length > 0 && lista.every(c => c.terminado);
  const R = 46; const TOTAL = 2 * Math.PI * R;
  const falta = Math.max(0, 100 - av.pct);
  const pendientesExamen = lista.filter(c => c.total > 0 && c.hechas === c.total && !c.terminado);

  const descargar = async () => {
    if (!cert?.id) return;
    setDescargando(true);
    try {
      const r = await certificadoService.descargarPDF(cert.id);
      const url = URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      const a = document.createElement('a'); a.href = url; a.download = `${mp.plan?.nombre || 'certificado'}.pdf`; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 4000);
    } catch (e) { /* el botón queda disponible para reintentar */ }
    setDescargando(false);
  };

  return (
    <Raiz $acento={acento} $fondo={fondo}>
      <Cabecera>
        {mp.plan?.imagen
          ? <img className="portada" src={urlImg(mp.plan.imagen)} alt="" />
          : <MuroCursos proyecto={proyecto} fondo={fondo} acento={acento} opacidad={0.55} />}
        <div>
          <span className="eyebrow">Mi plan de formación</span>
          <h1>{mp.plan?.nombre || 'Mi plan de formación'}</h1>
          {mp.plan?.descripcion && <p className="desc">{mp.plan.descripcion}</p>}
          <dl>
            <div><dd>{av.cursos_terminados} de {av.cursos_total}</dd><dt>{av.cursos_total === 1 ? 'curso terminado' : 'cursos terminados'}</dt></div>
            <div><dd>{av.hechas} de {av.total}</dd><dt>lecciones</dt></div>
            {av.tiempo_seg > 0 && <div><dd>{horas(av.tiempo_seg)}</dd><dt>de estudio</dt></div>}
          </dl>
        </div>
        <Avance $listo={completo} style={{ '--total': TOTAL, '--resta': TOTAL * (1 - Math.min(100, av.pct) / 100) }}>
          <span className="t">Mi avance en el plan de formación</span>
          <div className="anillo">
            <svg viewBox="0 0 100 100" aria-hidden="true"><circle className="base" cx="50" cy="50" r={R} /><circle className="valor" cx="50" cy="50" r={R} /></svg>
            <div className="cifra"><b>{av.pct}<small>%</small></b></div>
          </div>
          <figcaption>
            {completo
              ? <strong>Completaste tu plan de formación</strong>
              : av.pct >= 100
                ? <>Terminaste las lecciones; te falta aprobar {pendientesExamen.length === 1 ? 'un examen' : 'los exámenes pendientes'}.</>
                : <>Te falta el <strong>{falta} %</strong> para tu certificado final</>}
          </figcaption>
        </Avance>
      </Cabecera>

      <Cuerpo>
        <h2>Cursos del plan <small>{lista.length}</small></h2>
        <p>Puedes hacerlos en el orden que prefieras. Los que tienen examen cuentan como terminados al aprobarlo.</p>
        <Cursos>
          {lista.map(c => {
            const empezado = c.hechas > 0 || !!c.ultima;
            const clase = c.terminado ? 'listo' : empezado ? 'curso' : 'nuevo';
            const enlace = c.siguiente?.enlace || `/mentor/${c.documento_id}`;
            const faltaExamen = !c.terminado && c.total > 0 && c.hechas === c.total && c.examenes > c.examenes_aprobados;
            return (
              <Curso key={c.documento_id} $listo={c.terminado}>
                <span className={`estado ${clase}`} aria-hidden="true">{c.terminado ? Ico.ok : empezado ? Ico.medio : null}</span>
                <span className="img">{c.imagen && <img src={urlImg(c.imagen)} alt="" loading="lazy" />}</span>
                <div>
                  <h3>{c.titulo}</h3>
                  <div className="barra" aria-hidden="true"><i style={{ width: `${c.pct}%` }} /></div>
                  <div className="det">
                    <span><b>{c.terminado ? 'Terminado' : faltaExamen ? 'Falta el examen' : empezado ? `${c.pct} %` : 'Sin empezar'}</b> · {c.hechas} de {c.total} lecciones</span>
                    {c.examenes > 0 && <span>{c.examenes_aprobados} de {c.examenes} {c.examenes === 1 ? 'examen aprobado' : 'exámenes aprobados'}</span>}
                    {!c.terminado && empezado && c.siguiente && <span>Sigue: <b>{c.siguiente.titulo}</b></span>}
                    {c.ultima && <span>Última vez {haceCuanto(c.ultima)}</span>}
                  </div>
                </div>
                {c.terminado
                  ? <Link className="ir gris" to={`/mentor/${c.documento_id}`}>Repasar</Link>
                  : <Link className="ir blanco" to={enlace}>{Ico.play}{faltaExamen ? 'Presentar examen' : empezado ? 'Continuar' : 'Empezar'}</Link>}
              </Curso>
            );
          })}
        </Cursos>

        <Certificado $listo={!!cert?.emitido} aria-label="Certificado final del plan">
          <div className="diploma"><span>Certificado</span>{!cert?.emitido && <span className="candado" aria-hidden="true">{Ico.candado}</span>}</div>
          <div>
            <span className="eyebrow">{cert?.emitido ? 'Listo para descargar' : 'Certificado final del plan'}</span>
            <h3>{mp.plan?.nombre || 'Plan de formación'}</h3>
            <p>{cert?.emitido
              ? `Lo obtuviste al completar ${lista.length === 1 ? 'el curso' : `los ${lista.length} cursos`} de tu plan.${cert.codigo ? ` Código de verificación: ${cert.codigo}.` : ''}`
              : completo
                ? 'Completaste el plan. Tu certificado estará disponible muy pronto.'
                : `Se libera al completar ${lista.length === 1 ? 'el curso' : `los ${lista.length} cursos`} del plan, con sus exámenes aprobados. Te falta el ${falta} %.`}</p>
          </div>
          {cert?.emitido
            ? <button type="button" onClick={descargar} disabled={descargando}>{descargando ? 'Descargando…' : 'Descargar certificado'}</button>
            : <Link className="btn" to="/certificados" style={{ background: 'rgba(109,109,110,.6)', color: '#fff' }}>Mis certificados</Link>}
        </Certificado>
      </Cuerpo>
    </Raiz>
  );
};

export default MiPlanPage;
